import React from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Link, router, useForm } from '@inertiajs/react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

type Event = {
  id?: number;
  title: string;
  description: string;
  event_date: string;
  registration_url: string;
  position: number;
  published: boolean;
};

type Props = {
  event: Event;
  errors?: string[];
};

export default function EventForm({ event, errors }: Props) {
  const { data, setData, processing, submit } = useForm({
    title: event.title || '',
    description: event.description || '',
    event_date: event.event_date ? new Date(event.event_date).toISOString().slice(0, 16) : '',
    registration_url: event.registration_url || '',
    position: event.position || 0,
    published: event.published ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (event.id) {
      router.patch(`/admin/events/${event.id}`, data);
    } else {
      router.post('/admin/events', data);
    }
  };

  return (
    <AdminLayout title={event.id ? 'Edit Event' : 'New Event'}>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            href="/admin/events"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Events
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-gray-900">
            {event.id ? 'Edit Event' : 'New Event'}
          </h1>
        </div>

        {/* Error messages */}
        {errors && errors.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm font-medium text-red-800">Please fix the following errors:</p>
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white shadow rounded-lg p-6">
          <div>
            <Label htmlFor="title">Event Title *</Label>
            <Input
              id="title"
              type="text"
              value={data.title}
              onChange={(e) => setData('title', e.target.value)}
              required
              className="mt-1"
              placeholder="e.g., Personal Statement Workshop"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={data.description}
              onChange={(e) => setData('description', e.target.value)}
              rows={4}
              className="mt-1"
              placeholder="Brief description of the event..."
            />
          </div>

          <div>
            <Label htmlFor="event_date">Event Date & Time *</Label>
            <Input
              id="event_date"
              type="datetime-local"
              value={data.event_date}
              onChange={(e) => setData('event_date', e.target.value)}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="registration_url">Registration URL</Label>
            <Input
              id="registration_url"
              type="url"
              value={data.registration_url}
              onChange={(e) => setData('registration_url', e.target.value)}
              className="mt-1"
              placeholder="https://zoom.us/register/..."
            />
            <p className="mt-1 text-xs text-gray-500">
              Link to registration form or meeting
            </p>
          </div>

          <div>
            <Label htmlFor="position">Position (Order)</Label>
            <Input
              id="position"
              type="number"
              value={data.position}
              onChange={(e) => setData('position', parseInt(e.target.value) || 0)}
              className="mt-1"
              min="0"
            />
            <p className="mt-1 text-xs text-gray-500">
              Lower numbers appear first
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Label htmlFor="published" className="text-base font-medium">
                Published
              </Label>
              <Switch
                id="published"
                checked={data.published}
                onCheckedChange={(checked) => setData('published', checked)}
              />
            </div>
            <p className="text-sm text-gray-500">
              {data.published ? 'Event is visible to users' : 'Event is hidden from users'}
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button type="button" variant="outline" asChild>
              <Link href="/admin/events">Cancel</Link>
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? 'Saving...' : 'Save Event'}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}